import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSearchFiltersMutation } from "../../../Redux/Api/ProductAPI";
import defaultImage from "../../../assets/defaultImage.svg";
export interface SheetsData {
    categoryId: string;
    categoryName: string;
    images: string[];
}

interface Props {
    open: boolean;
    handleOpen: () => void;
    sheets?: SheetsData[];
}

const CategoriesList = ({ open, handleOpen, sheets }: Props) => {
    const navigate = useNavigate();
    const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [products, setProducts] = useState<any[]>([]);

    const [combinedState, setCombinedState] = useState<{
        search: null;
        company: string[];
        category: string[];
        limit: number;
        page: number;
    }>({
        search: null,
        company: [],
        category: [],
        limit: 100,
        page: 1,
    });

    const [searchFilters, { isLoading }] = useSearchFiltersMutation();

    useEffect(() => {
        if (open && sheets && sheets.length > 0) {
            const firstCategoryId = sheets[0].categoryId;
            if (selectedCategoryId !== firstCategoryId) {
                handleCategoryClick(firstCategoryId);
            }
        }
    }, [open, sheets]);

    useEffect(() => {
        document.body.classList.toggle("overflow-hidden", open);
        return () => document.body.classList.remove("overflow-hidden");
    }, [open]);

    useEffect(() => {
        if (selectedCategoryId && cardRefs.current[selectedCategoryId]) {
            cardRefs.current[selectedCategoryId]?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [selectedCategoryId]);

    const handleCategoryClick = async (categoryId: string) => {
        if (categoryId === selectedCategoryId) return;

        setSelectedCategoryId(categoryId);

        setProducts([]);

        const newCombinedState = { ...combinedState, category: [categoryId], page: 1 };
        setCombinedState(newCombinedState);

        try {
            const res: any = await searchFilters({ page: 1, combinedState: newCombinedState }).unwrap();
            setProducts(res?.products || []);
        } catch (error) {
            toast.error("Failed to fetch products");
            setProducts([]);
        }
    };

    const handleItemClick = (id: string | null, name: string | null) => {
        navigate(`/product/${id}/${name}`, { state: { id } });
    };

    return (
        <>
            {open && (
                <div
                    onClick={handleOpen}
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
                />
            )}
            <div
                className={`h-[600px] w-full bg-white rounded-t-3xl z-50 fixed bottom-0 transition-transform duration-300 ${open ? "translate-y-0" : "translate-y-[calc(100%)]"
                    } overflow-hidden`}
            >
                <div className="w-full h-full relative flex flex-col">
                    {/* Header */}
                    <div className="px-6 py-5 bg-gray-50 rounded-t-3xl shadow-md flex items-center sticky top-0 z-50">
                        <h4 className="text-[17px] font-semibold text-gray-800">Sheets Categories</h4>
                    </div>

                    {/* Body */}
                    <div className="flex w-full flex-1 overflow-hidden">
                        {/* Left Categories */}
                        <div className="flex flex-col w-28 bg-white overflow-y-auto no-scrollbar border-r border-gray-200 ">
                            {sheets?.map((sheet) => (
                                <div
                                    key={sheet.categoryId}
                                    onClick={() => handleCategoryClick(sheet.categoryId)}
                                    className="p-2 transition cursor-pointer flex flex-col items-center"
                                >
                                    <div
                                        className={`border-2 rounded-full p-2 w-16 h-16 flex justify-center items-center ${selectedCategoryId === sheet.categoryId
                                            ? "border-gray-500"
                                            : "border-gray-200"
                                            }`}
                                    >
                                        <img src={sheet.images[0]} alt="" className="object-contain w-12 h-12" />
                                    </div>
                                    <span className="mt-2 text-[12px] text-center font-bold">
                                        {sheet.categoryName.split(" ")[0]}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {/* Right Products */}
                        <div className="w-full p-2 overflow-y-auto no-scrollbar h-full flex flex-col gap-3">
                            {isLoading && (
                                <div className=" h-full text-xs flex justify-center items-center py-10">
                                    <svg
                                        className="animate-spin h-12 w-12 text-gray-600"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                        ></path>
                                    </svg>
                                </div>
                            )}

                            {/* No Products Found */}
                            {!isLoading && products.length === 0 && selectedCategoryId && (
                                <div className=" h-full flex justify-center text-center text-xs items-center py-10 text-gray-500 italic font-semibold">
                                    No products available in this category yet.
                                </div>
                            )}
                            {!isLoading &&
                                products.map((product) => (
                                    <div
                                        key={product?.id}
                                        onClick={() => handleItemClick(product?.id, product?.productName)}
                                        ref={(el) => (cardRefs.current[product._id] = el)}
                                        className="flex flex-row p-2 w-full cursor-pointer rounded-md bg-white border border-gray-200 shadow hover:shadow-md transition-all duration-300 items-center gap-4"
                                    >
                                        <div className="flex-shrink-0 h-20 w-20 bg-[#56A2B04A] border rounded-md overflow-hidden flex items-center justify-center relative">
                                            <img
                                                src={
                                                    Array.isArray(product.images) && product.images.length > 0
                                                        ? product.images[0]
                                                        : typeof product.images === "string"
                                                            ? product.images
                                                            : defaultImage
                                                }
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null; 
                                                    e.currentTarget.src = defaultImage;
                                                }}
                                                alt={product.productName || "Product Image"}
                                                className="object-cover object-center w-full h-full rounded-md"
                                            />
                                        </div>
                                        <div className="flex-1 flex flex-col justify-center">
                                            <p className="font-medium text-gray-800 text-[12px] line-clamp-2">
                                                {product.productName || "Unnamed Product"}
                                            </p>

                                            {product?.status ? (
                                                <button className="mt-1 text-[8px] text-white bg-green-500 px-3 py-0.5 rounded-full hover:bg-green-600 w-max">
                                                    In Stock
                                                </button>
                                            ) : (
                                                <button className="mt-1 text-[8px] text-white bg-red-500 px-3 py-0.5 rounded-full hover:bg-red-600 w-max">
                                                    Out of Stock
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default CategoriesList;
